import { useState, useEffect, useMemo } from "react";
import { Link, Outlet, useOutlet, useSearchParams, useNavigate } from "react-router-dom";
import { ContainerStyles, TextStyles, FeedbackStyles } from "styles/style";
import { utils } from "supabase/content/utils.ts";
import BaseView from "./BaseView";
import { api } from "supabase/database/api.ts";
import styled from "styled-components";
import tw from "twin.macro";
import { StyledModal, ModalHeading, ModalButton } from "pages/authPages/AuthStyles";

const PasswordInput = tw.input`w-full px-4 py-3 rounded-lg font-medium bg-gray-100 border border-gray-200 placeholder-gray-500 text-sm focus:outline-none focus:border-gray-400 focus:bg-white mt-2`;
const PasswordError = tw.p`text-red-500 text-sm mt-2 text-center`;
const CancelLink = tw.button`text-gray-500 text-sm mt-3 underline w-full text-center cursor-pointer bg-transparent border-none`;

const UNLOCKED_KEY = "lra_unlocked_routes";

const isUnlocked = (path) =>
  JSON.parse(sessionStorage.getItem(UNLOCKED_KEY) || "[]").includes(path);

const unlockRoute = (path) => {
  const current = JSON.parse(sessionStorage.getItem(UNLOCKED_KEY) || "[]");
  if (!current.includes(path))
    sessionStorage.setItem(UNLOCKED_KEY, JSON.stringify([...current, path]));
};

export default function ListView({
  title = null,
  list = null,
  table = null,
  url = null,
  sort_by = null,
}) {
  const navigate = useNavigate();
  const [material, setMaterial] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const pageSize = 15;
  const [capLength, setCapLength] = useState(63);
  const hasChild = useOutlet();
  const [passwordModal, setPasswordModal] = useState({ open: false, item: null });
  const [passwordInput, setPasswordInput] = useState("");
  const [passwordError, setPasswordError] = useState("");

  const openPasswordModal = (item) => {
    setPasswordModal({ open: true, item });
    setPasswordInput("");
    setPasswordError("");
  };

  const closePasswordModal = () => setPasswordModal({ open: false, item: null });

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    const { item } = passwordModal;
    if (passwordInput === item.password) {
      unlockRoute(item.path);
      closePasswordModal();
      navigate(item.path);
    } else {
      setPasswordError("Incorrect password. Please try again.");
    }
  };

  // Get page and search from URL params
  const currentPage = parseInt(searchParams.get("page") || "1", 10);
  const currentSearch = searchParams.get("search") || "";

  // Fetch all data once — merge child routes and DB content when both provided
  useEffect(() => {
    async function prepMaterial() {
      let items = list ? [...list] : [];
      if (table) {
        const data = await api.contentQueries.getContentList({ table, sort_by: sort_by ?? undefined });
        items = [...items, ...(data || [])];
      }
      setMaterial(items);
      setLoading(false);
    }
    prepMaterial();
  }, [list, table]);

  // Handle search change
  const handleSearchChange = (newSearch) => {
    setSearchParams(
      (prev) => {
        const newParams = new URLSearchParams(prev);
        if (newSearch.trim()) {
          newParams.set("search", newSearch);
        } else {
          newParams.delete("search");
        }
        // Always reset to page 1 when searching
        if (newSearch.trim() !== currentSearch) {
          newParams.set("page", "1");
        }
        return newParams;
      },
      { replace: true },
    );
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.set("page", newPage.toString());
      return newParams;
    });
  };

  // Responsive title length
  useEffect(() => {
    const updateCapLength = () => {
      const width = window.innerWidth;
      if (width <= 640) setCapLength(63);
      else if (width <= 768) setCapLength(110);
      else setCapLength(180);
    };
    updateCapLength();
    window.addEventListener("resize", updateCapLength);
    return () => window.removeEventListener("resize", updateCapLength);
  }, []);

  // Filter + paginate in memory
  const filtered = useMemo(() => {
    return material.filter(
      (item) =>
        item.title?.toLowerCase().includes(currentSearch.toLowerCase()) ||
        item.date?.toLowerCase().includes(currentSearch.toLowerCase()),
    );
  }, [material, currentSearch]);

  const paginated = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, currentPage]);

  return (
    <>
      {hasChild ? (
        <Outlet />
      ) : (
        <BaseView
          title={title}
          listCount={filtered.length}
          search={currentSearch}
          onSearchChange={handleSearchChange}
          page={currentPage}
          onPageChange={handlePageChange}
          pageSize={pageSize}
        >
          {loading ? (
            <ContainerStyles.Posts>
              {Array.from({ length: 5 }).map((_, i) => (
                <ContainerStyles.PostContainer key={i} featured={true}>
                  <TextStyles.Post>
                    <TextStyles.Info>
                      <FeedbackStyles.SkeletonDateLine />
                      <FeedbackStyles.SkeletonLine />
                      <FeedbackStyles.SkeletonShortLine />
                    </TextStyles.Info>
                  </TextStyles.Post>
                </ContainerStyles.PostContainer>
              ))}
            </ContainerStyles.Posts>
          ) : filtered.length === 0 ? (
            <FeedbackStyles.EmptyStateContainer>
              <FeedbackStyles.EmptyStateTitle>No content found</FeedbackStyles.EmptyStateTitle>
              {currentSearch && (
                <FeedbackStyles.EmptyStateHint>Try a different search term</FeedbackStyles.EmptyStateHint>
              )}
            </FeedbackStyles.EmptyStateContainer>
          ) : (
            <ContainerStyles.Posts>
              {paginated.map((item, index) => {
                const protected_ = item.password && !isUnlocked(item.path);
                return (
                  <ContainerStyles.PostContainer key={index} featured={true}>
                    <TextStyles.Post
                      className="group"
                      as={protected_ ? "div" : Link}
                      to={protected_ ? undefined : `${item.path || item.id}`}
                      onClick={protected_ ? () => openPasswordModal(item) : undefined}
                      style={protected_ ? { cursor: "pointer" } : undefined}
                    >
                      <TextStyles.Info>
                        {item.date && (
                          <TextStyles.Category>
                            {utils.formattedDate(item.date)}
                          </TextStyles.Category>
                        )}
                        <TextStyles.Title>
                          {item.title.length > capLength
                            ? item.title.slice(0, capLength) + "..."
                            : item.title}
                        </TextStyles.Title>
                        {item.region && item.region.toLowerCase() !== "all" && (
                          <TextStyles.RegionBadge>{item.region.toUpperCase()}</TextStyles.RegionBadge>
                        )}
                      </TextStyles.Info>
                    </TextStyles.Post>
                  </ContainerStyles.PostContainer>
                );
              })}
            </ContainerStyles.Posts>
          )}
        </BaseView>
      )}

      <StyledModal
        isOpen={passwordModal.open}
        onRequestClose={closePasswordModal}
        shouldCloseOnOverlayClick={true}
      >
        <ModalHeading>Protected Content</ModalHeading>
        <p tw="text-gray-600 text-sm mb-2">
          Enter the password to access <strong>{passwordModal.item?.title}</strong>.
        </p>
        <form onSubmit={handlePasswordSubmit}>
          <PasswordInput
            type="password"
            placeholder="Password"
            value={passwordInput}
            onChange={(e) => { setPasswordInput(e.target.value); setPasswordError(""); }}
            autoFocus
          />
          {passwordError && <PasswordError>{passwordError}</PasswordError>}
          <ModalButton primary type="submit" tw="mt-4">Unlock</ModalButton>
        </form>
        <CancelLink type="button" onClick={closePasswordModal}>Cancel</CancelLink>
      </StyledModal>
    </>
  );
}
